import { AISchemaType } from "components/server-types";

/**
 * AIPseudoBlockType is our abstraction for worksheet blocks as represented
 * to the AI. Lots of the complexity of the worksheet schema is due to implementation
 * details, and AI tends to mess up the implementation details. They are also hard
 * to validate, since the schema is complex. This is our attempt to simplify the
 * schema for the AI, with a deterministic mapping from/to the worksheet schema.
 *
 * However, converting a real worksheet back and forth _will_ cause some discrepancies,
 * so this can't be used to get the AI to make fine-grained changes to the worksheet,
 * since the diff would also contain more (though usually superficial) changes.
 */
export type AIPseudoBlockType =
  | {
      block_type: "multiple_choice_single_answer" | "multiple_choice_multiple_answers";
      instructions: string;
      data: string[];
    }
  | {
      block_type: "table";
      instructions: string;
      data: string[][];
    }
  | {
      block_type: "free_response";
      instructions: string;
      data: null;
    }
  | {
      block_type: "file_upload";
      instructions: string;
      data: null;
    }
  | {
      block_type: "whiteboard";
      instructions: string;
      data: null;
    };

const instructionsSchema = {
  type: "string",
  description: "Markdown string for instructions.",
};

/**
 * JSON schema for the {blocks: AIPseudoBlockType[]}
 */
export const worksheetSchema: AISchemaType = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  type: "object",
  description: "Defines the blocks for a Page.",
  additionalProperties: false,
  properties: {
    blocks: {
      type: "array",
      description:
        "The list of blocks for the Page. Any blocks already present should be included as-is, otherwise " +
        "they will be seen as a suggestion to remove them.",
      minItems: 1,
      maxItems: 10,
      items: {
        oneOf: [
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["multiple_choice_single_answer"] },
              instructions: instructionsSchema,
              data: {
                type: "array",
                items: { type: "string" },
                description:
                  "Array of choice strings, plaintext, without any prefix (e.g. 'a)' or '1.')",
              },
            },
            required: ["block_type", "instructions", "data"],
          },
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["multiple_choice_multiple_answers"] },
              instructions: instructionsSchema,
              data: {
                type: "array",
                items: { type: "string" },
                description:
                  "Array of choice strings, plaintext, without any prefix (e.g. 'a)' or '1.')",
              },
            },
            required: ["block_type", "instructions", "data"],
          },
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["free_response"] },
              instructions: instructionsSchema,
              data: {
                type: "null",
                description: "No data required for free response; students can fill in the blank.",
              },
            },
            required: ["block_type", "instructions", "data"],
          },
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["table"] },
              instructions: instructionsSchema,
              data: {
                type: "array",
                items: { type: "array", items: { type: "string" } },
                description:
                  "2D array of markdown strings where first row/column are headers (not modifiable by students). " +
                  "Only the fields that are modifiable by students can be filled in.",
              },
            },
            required: ["block_type", "data"],
          },
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["file_upload"] },
              instructions: instructionsSchema,
              data: {
                type: "null",
                description: "No data required for file upload; students can upload a file.",
              },
            },
            required: ["block_type", "instructions", "data"],
          },
          {
            type: "object",
            additionalProperties: false,
            properties: {
              block_type: { type: "string", enum: ["whiteboard"] },
              instructions: instructionsSchema,
              data: {
                type: "null",
                description:
                  "No data required for whiteboard; students can draw on the whiteboard.",
              },
            },
            required: ["block_type", "data"],
          },
        ],
      },
    },
  },
  required: ["blocks"],
};
